// components/project/ProjectSiteActionsCard.js
// Card "Gestione Cantiere" per azioni guidate stato progetto - ABITALO CRM

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import { formatDate, formatPercentage } from '../../lib/utils';
import {
  Building2,
  Play,
  Pause,
  PlayCircle,
  CheckCircle,
  XCircle,
  Lock,
  AlertTriangle,
  FileText,
  Calendar,
  Clock,
  ChevronDown,
  ChevronUp,
  Settings
} from 'lucide-react';

const ProjectSiteActionsCard = ({ projectId, onStatusChange, refreshTrigger }) => {
  const { user, isAdmin, isAdministration } = useAuth();
  
  const [siteStatus, setSiteStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Dialog states
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [showCompleteWorksDialog, setShowCompleteWorksDialog] = useState(false);
  const [showCloseProjectDialog, setShowCloseProjectDialog] = useState(false);
  
  // Form states
  const [suspendReason, setSuspendReason] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Expanded state for admin fallback dropdown
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  const loadSiteStatus = useCallback(async () => {
    try {
      const response = await fetch(`/api/projects/${projectId}/site-status`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSiteStatus(data);
      }
    } catch (err) {
      console.error('Error loading site status:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadSiteStatus();
  }, [loadSiteStatus, refreshTrigger]);

  // Execute action
  const executeAction = async (actionType, body = null) => {
    setActionLoading(true);
    try {
      const response = await fetch(`/api/projects/${projectId}/actions/${actionType}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: body ? JSON.stringify(body) : null
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Errore nell\'esecuzione dell\'azione');
      }
      
      const result = await response.json();
      
      // Show success message
      toast.success(result.message);
      
      // Show warning if present
      if (result.warning) {
        toast.warning(result.warning, { duration: 6000 });
      }
      
      // Reload status and notify parent
      await loadSiteStatus();
      if (onStatusChange) {
        onStatusChange();
      }
      
      return result;
    } catch (error) {
      toast.error(error.message);
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  // Action handlers
  const handleCompleteChoices = async () => {
    await executeAction('complete-choices');
  };

  const handleStartSite = async () => {
    await executeAction('start-site', { start_date: startDate });
    setShowStartDialog(false);
  };

  const handleSuspendSite = async () => {
    if (!suspendReason.trim()) {
      toast.error('Inserisci il motivo della sospensione');
      return;
    }
    await executeAction('suspend-site', { reason: suspendReason.trim() });
    setShowSuspendDialog(false);
    setSuspendReason('');
  };

  const handleResumeSite = async () => {
    await executeAction('resume-site');
  };

  const handleCompleteWorks = async () => {
    await executeAction('complete-works');
    setShowCompleteWorksDialog(false);
  };

  const handleCloseProject = async () => {
    await executeAction('close-project');
    setShowCloseProjectDialog(false);
  };

  // Status badge colors
  const getStatusColor = (status) => {
    const colors = {
      'in_attesa_acconto': 'bg-amber-100 text-amber-800 border-amber-300',
      'acconto_da_fatturare': 'bg-blue-100 text-blue-800 border-blue-300',
      'acconto_pagato': 'bg-green-100 text-green-800 border-green-300',
      'in_attesa_scelte': 'bg-purple-100 text-purple-800 border-purple-300',
      'scelte_completate': 'bg-indigo-100 text-indigo-800 border-indigo-300',
      'in_attesa_materiali': 'bg-orange-100 text-orange-800 border-orange-300',
      'pronto_partenza': 'bg-cyan-100 text-cyan-800 border-cyan-300',
      'cantiere_avviato': 'bg-emerald-100 text-emerald-800 border-emerald-300',
      'in_corso': 'bg-green-100 text-green-800 border-green-300',
      'sospeso': 'bg-red-100 text-red-800 border-red-300',
      'completato': 'bg-blue-100 text-blue-800 border-blue-300',
      'chiuso': 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  // Acconto status badge
  const getAccontoStatusBadge = (status) => {
    const badges = {
      'incassato': { label: 'Incassato', color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle },
      'fatturato': { label: 'Fatturato', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: FileText },
      'da_fatturare': { label: 'Da fatturare', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock },
      'non_sbloccato': { label: 'Non sbloccato', color: 'bg-gray-100 text-gray-600 border-gray-300', icon: Lock },
      'non_presente': { label: 'Non presente', color: 'bg-red-100 text-red-600 border-red-300', icon: XCircle }
    };
    return badges[status] || badges['non_presente'];
  };

  // Practice status badge
  const getPracticeStatusBadge = (status) => {
    const badges = {
      'validata': { label: 'Validata', color: 'bg-green-100 text-green-700 border-green-300' },
      'compilata': { label: 'Compilata', color: 'bg-blue-100 text-blue-700 border-blue-300' },
      'non_necessaria': { label: 'Non necessaria', color: 'bg-gray-100 text-gray-600 border-gray-300' },
      'da_compilare': { label: 'Da compilare', color: 'bg-amber-100 text-amber-700 border-amber-300' }
    };
    return badges[status] || badges['da_compilare'];
  };

  // Render action button
  const renderActionButton = (action) => {
    const icons = {
      'complete_choices': CheckCircle,
      'start_site': Play,
      'suspend_site': Pause,
      'resume_site': PlayCircle,
      'complete_works': CheckCircle,
      'close_project': XCircle
    };
    
    const colors = {
      'complete_choices': 'bg-indigo-600 hover:bg-indigo-700 text-white',
      'start_site': 'bg-emerald-600 hover:bg-emerald-700 text-white',
      'suspend_site': 'bg-amber-600 hover:bg-amber-700 text-white',
      'resume_site': 'bg-green-600 hover:bg-green-700 text-white',
      'complete_works': 'bg-blue-600 hover:bg-blue-700 text-white',
      'close_project': 'bg-gray-600 hover:bg-gray-700 text-white'
    };
    
    const Icon = icons[action.action] || CheckCircle;
    const colorClass = colors[action.action] || 'bg-gray-600 hover:bg-gray-700 text-white';
    
    const handleClick = () => {
      switch (action.action) {
        case 'complete_choices':
          handleCompleteChoices();
          break;
        case 'start_site':
          setStartDate(new Date().toISOString().split('T')[0]);
          setShowStartDialog(true);
          break;
        case 'suspend_site':
          setSuspendReason('');
          setShowSuspendDialog(true);
          break;
        case 'resume_site':
          handleResumeSite();
          break;
        case 'complete_works':
          if (siteStatus?.sal_percentage < 100) {
            setShowCompleteWorksDialog(true);
          } else {
            handleCompleteWorks();
          }
          break;
        case 'close_project':
          setShowCloseProjectDialog(true);
          break;
        default:
          break;
      }
    };

    return (
      <div key={action.action} className="flex flex-col gap-1">
        <Button
          onClick={handleClick}
          disabled={!action.enabled || actionLoading}
          className={`${action.enabled ? colorClass : 'bg-gray-300 text-gray-500 cursor-not-allowed'} transition-all`}
          data-testid={`action-btn-${action.action}`}
        >
          <Icon className="w-4 h-4 mr-2" />
          {action.label}
        </Button>
        {!action.enabled && action.disabled_reason && (
          <span className="text-xs text-red-600 flex items-center gap-1">
            <Lock className="w-3 h-3" />
            {action.disabled_reason}
          </span>
        )}
        {action.warning && action.enabled && (
          <span className="text-xs text-amber-600 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            {action.action === 'complete_works'
              ? `SAL non completato: ${formatPercentage(siteStatus?.sal_percentage)}`
              : action.warning}
          </span>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="border-[#DCDCDC]">
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin w-6 h-6 border-2 border-[#FF7819] border-t-transparent rounded-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!siteStatus) {
    return null;
  }

  const accontoBadge = getAccontoStatusBadge(siteStatus.acconto_status);
  const AccontoIcon = accontoBadge.icon;

  return (
    <>
      <Card className="card-abitalo h-full" data-testid="site-actions-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#FF7819]" />
              <CardTitle className="text-base">Gestione Cantiere</CardTitle>
            </div>
            <Badge className={`${getStatusColor(siteStatus.status)} border`}>
              {siteStatus.status_label}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-3">
          {/* Info Row — SAL e Pratica sono già in Panoramica: qui solo dati operativi */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            {/* Data Inizio Lavori */}
            <div className="flex flex-col">
              <span className="text-[#737373] text-xs mb-1">Data inizio lavori</span>
              <span className="font-medium flex items-center gap-1">
                <Calendar className="w-4 h-4 text-[#737373]" />
                {siteStatus.start_date ? formatDate(siteStatus.start_date) : 'Non avviato'}
              </span>
            </div>
            
            {/* Acconto */}
            <div className="flex flex-col">
              <span className="text-[#737373] text-xs mb-1">Acconto/Firma Contratto</span>
              <Badge className={`${accontoBadge.color} border w-fit text-xs`}>
                <AccontoIcon className="w-3 h-3 mr-1" />
                {accontoBadge.label}
              </Badge>
            </div>
          </div>
          
          <Separator />
          
          {/* Actions */}
          <div>
            <h4 className="text-sm font-medium text-[#737373] mb-3">Azioni disponibili</h4>
            
            {siteStatus.available_actions?.length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {siteStatus.available_actions.map(action => renderActionButton(action))}
              </div>
            ) : (
              <p className="text-sm text-[#737373] italic">
                {siteStatus.status === 'chiuso' 
                  ? 'Progetto chiuso - nessuna azione disponibile'
                  : 'Nessuna azione disponibile per lo stato corrente'}
              </p>
            )}
          </div>
          
          {/* Last Event */}
          {siteStatus.last_event && (
            <div className="text-xs text-[#737373] pt-2 border-t border-dashed border-[#DCDCDC]">
              <span className="font-medium">Ultimo evento:</span> {siteStatus.last_event.title} ({siteStatus.last_event.date} {siteStatus.last_event.time})
            </div>
          )}
          
          {/* Admin Fallback Dropdown */}
          {siteStatus.can_modify_status_dropdown && (
            <div className="pt-3 border-t border-[#DCDCDC]">
              <button
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                className="flex items-center gap-2 text-xs text-[#737373] hover:text-[#FF7819] transition-colors"
                data-testid="toggle-advanced-options"
              >
                <Settings className="w-4 h-4" />
                Modifica stato avanzata (Admin)
                {showAdvancedOptions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              
              {showAdvancedOptions && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-amber-600 mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Usa questa opzione solo se necessario. Le azioni guidate garantiscono tracciabilità completa.
                  </p>
                  {/* Il dropdown viene renderizzato dal componente padre per mantenere la compatibilità */}
                  <div id="admin-status-dropdown-placeholder" />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog: Avvia Cantiere */}
      <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-emerald-600" />
              Avvia Cantiere
            </DialogTitle>
            <DialogDescription>
              Conferma l'avvio del cantiere e la data di inizio lavori.
            </DialogDescription>
          </DialogHeader>
          
          {/* Warning pratica edilizia */}
          {siteStatus?.practice_status === 'da_compilare' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Pratica edilizia non compilata</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Puoi procedere, ma il dato sarà necessario per la fatturazione.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="start-date">Data inizio lavori</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStartDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleStartSite} 
              disabled={actionLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {actionLoading ? 'Avvio...' : 'Avvia Cantiere'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Sospendi Cantiere */}
      <Dialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pause className="w-5 h-5 text-amber-600" />
              Sospendi Cantiere
            </DialogTitle>
            <DialogDescription>
              Inserisci il motivo della sospensione. Questo verrà registrato nel Diario di Cantiere.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="suspend-reason">Motivo sospensione *</Label>
              <Textarea
                id="suspend-reason"
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="Es: Attesa materiali, condizioni meteo avverse, richiesta cliente..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSuspendDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleSuspendSite} 
              disabled={actionLoading || !suspendReason.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {actionLoading ? 'Sospensione...' : 'Sospendi Cantiere'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Chiudi Lavori (warning SAL < 100%) */}
      <Dialog open={showCompleteWorksDialog} onOpenChange={setShowCompleteWorksDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Conferma Chiusura Lavori
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Attenzione:</strong> Il SAL è attualmente al <strong>{siteStatus?.sal_percentage?.toFixed(1)}%</strong>, non al 100%.
              </p>
              <p className="text-sm text-amber-700 mt-2">
                Vuoi comunque segnare i lavori come completati?
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompleteWorksDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleCompleteWorks} 
              disabled={actionLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {actionLoading ? 'Completamento...' : 'Conferma Completamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Chiudi Progetto */}
      <Dialog open={showCloseProjectDialog} onOpenChange={setShowCloseProjectDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-gray-600" />
              Chiudi Progetto
            </DialogTitle>
            <DialogDescription>
              Questa azione chiuderà definitivamente il progetto a livello amministrativo.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-[#737373]">
              Una volta chiuso, il progetto non potrà più essere modificato. Sei sicuro di voler procedere?
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseProjectDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleCloseProject} 
              disabled={actionLoading}
              className="bg-gray-600 hover:bg-gray-700 text-white"
            >
              {actionLoading ? 'Chiusura...' : 'Chiudi Progetto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProjectSiteActionsCard;
